import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { Plus, Pencil, Trash2, Loader2, GripVertical } from 'lucide-react'
import { Button, Input, Modal, LoadingState, ErrorState, EmptyState } from '@/components/ui'

// ─── Types ───────────────────────────────────────────────────────────────────

interface ServiceCatalogCategory {
  id: string
  name: string
  description?: string
  sortOrder: number
  isActive: boolean
}

// ─── Category Form Modal ──────────────────────────────────────────────────────

function CategoryFormModal({
  open,
  onOpenChange,
  category,
  onSaved,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  category: ServiceCatalogCategory | null
  onSaved: () => void
}) {
  const queryClient = useQueryClient()
  const isNew = !category
  const [name, setName] = useState(category?.name ?? '')
  const [description, setDescription] = useState(category?.description ?? '')
  const [error, setError] = useState<string | null>(null)

  const saveMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      isNew
        ? apiClient.post('/service-catalog/categories', data)
        : apiClient.patch(`/service-catalog/categories/${category!.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-catalog-categories'] })
      onSaved()
      onOpenChange(false)
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: string } }; message?: string }
      setError(e.response?.data?.error ?? e.message ?? 'Fehler beim Speichern')
    },
  })

  function handleSubmit() {
    if (!name.trim()) { setError('Name ist Pflichtfeld'); return }
    setError(null)
    saveMutation.mutate({ name: name.trim(), description: description.trim() || undefined })
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isNew ? 'Neue Kategorie' : 'Kategorie bearbeiten'}
      size="sm"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Kategoriename"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Beschreibung</label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Kurze Beschreibung (optional)"
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {isNew ? 'Anlegen' : 'Speichern'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Main Section ─────────────────────────────────────────────────────────────

export function ServicekatalogCategoriesSection() {
  const queryClient = useQueryClient()
  const [editCategory, setEditCategory] = useState<ServiceCatalogCategory | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<ServiceCatalogCategory | null>(null)

  const { data: categories = [], isLoading, error } = useQuery<ServiceCatalogCategory[]>({
    queryKey: ['service-catalog-categories'],
    queryFn: () => apiClient.get('/service-catalog/categories').then((r) => r.data),
    retry: false,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/service-catalog/categories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-catalog-categories'] })
      setDeleteConfirm(null)
    },
  })

  if (isLoading) return <LoadingState message="Kategorien werden geladen…" />
  if (error) {
    const e = error as { response?: { data?: { error?: string } }; message?: string }
    return <ErrorState message={e.response?.data?.error ?? e.message ?? 'Fehler beim Laden'} />
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Servicekatalog-Kategorien</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {categories.length} Kategorien — werden im Servicekatalog und bei SAP-Syncs verwendet
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => { setEditCategory(null); setFormOpen(true) }}
        >
          <Plus className="w-4 h-4 mr-1" />
          Neue Kategorie
        </Button>
      </div>

      {categories.length === 0 ? (
        <EmptyState
          title="Keine Kategorien"
          description="Lege die erste Kategorie an."
          action={
            <Button variant="primary" size="sm" onClick={() => { setEditCategory(null); setFormOpen(true) }}>
              <Plus className="w-4 h-4 mr-1" />
              Anlegen
            </Button>
          }
        />
      ) : (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
          {categories.map((cat) => (
            <div key={cat.id} className="flex items-center gap-3 px-4 py-3">
              <GripVertical className="w-4 h-4 text-gray-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{cat.name}</p>
                {cat.description && (
                  <p className="text-xs text-gray-500 truncate">{cat.description}</p>
                )}
              </div>
              <span className="text-xs text-gray-400">#{cat.sortOrder}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setEditCategory(cat); setFormOpen(true) }}
                  className="p-1.5 text-gray-400 hover:text-brand-600 rounded transition-colors"
                  title="Bearbeiten"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setDeleteConfirm(cat)}
                  className="p-1.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                  title="Löschen"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CategoryFormModal
        open={formOpen}
        onOpenChange={(v) => {
          setFormOpen(v)
          if (!v) setEditCategory(null)
        }}
        category={editCategory}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['service-catalog-categories'] })}
      />

      {deleteConfirm && (
        <Modal
          open={!!deleteConfirm}
          onOpenChange={() => setDeleteConfirm(null)}
          title="Kategorie löschen"
          size="sm"
        >
          <p className="text-sm text-gray-700 mb-4">
            Soll die Kategorie <strong>{deleteConfirm.name}</strong> wirklich gelöscht werden?
            Zugeordnete Einträge werden dadurch nicht gelöscht, verlieren aber die Kategorie-Zuordnung.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>Abbrechen</Button>
            <Button
              variant="danger"
              onClick={() => deleteMutation.mutate(deleteConfirm.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Löschen
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
